import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

const MIN_WIDTH = 60

type Widths = Record<string, number | undefined>

function loadWidths(storageKey: string): Widths {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      const clean: Widths = {}
      Object.entries(parsed).forEach(([k, v]) => {
        if (typeof v === 'number' && Number.isFinite(v)) clean[k] = Math.max(MIN_WIDTH, v)
      })
      return clean
    }
  } catch {
    // corrupt storage — fall back to auto widths
  }
  return {}
}

/**
 * Drag-to-resize column widths for a table, persisted to localStorage.
 * Attach `onMouseDown={startResize(colKey)}` to a handle inside each <th>
 * (the <th> itself is measured as the drag baseline).
 */
export function useColumnResize(storageKey: string) {
  const [widths, setWidths] = useState<Widths>(() => loadWidths(storageKey))
  const dragRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null)
  const widthsRef = useRef(widths)
  widthsRef.current = widths
  // Cleanup for the in-progress drag (if any) — invoked by mouseup, or by the
  // unmount effect below if the table unmounts mid-drag (route/tab change).
  const activeCleanupRef = useRef<(() => void) | null>(null)

  // Re-load when the storage key changes (e.g. navigating between companies)
  useEffect(() => {
    setWidths(loadWidths(storageKey))
  }, [storageKey])

  // Guarantee listener/style cleanup even if the component unmounts mid-drag
  useEffect(() => {
    return () => activeCleanupRef.current?.()
  }, [])

  const startResize = useCallback(
    (key: string) => (e: ReactMouseEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const th = (e.currentTarget as HTMLElement).closest('th')
      const startWidth = widthsRef.current[key] ?? th?.getBoundingClientRect().width ?? 150
      dragRef.current = { key, startX: e.clientX, startWidth }

      const onMove = (ev: MouseEvent) => {
        const drag = dragRef.current
        if (!drag) return
        const next = Math.max(MIN_WIDTH, Math.round(drag.startWidth + (ev.clientX - drag.startX)))
        // Update the ref synchronously (not just via the render-driven sync
        // below) so onUp's persist always sees the latest drag position even
        // if mouseup fires before React re-renders.
        widthsRef.current = { ...widthsRef.current, [drag.key]: next }
        setWidths(widthsRef.current)
      }
      const onUp = () => {
        dragRef.current = null
        activeCleanupRef.current = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        try {
          localStorage.setItem(storageKey, JSON.stringify(widthsRef.current))
        } catch {
          // storage full/unavailable — widths still apply for this session
        }
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      activeCleanupRef.current = onUp
    },
    [storageKey],
  )

  return { widths, startResize }
}
